<p-toast position="top-center"></p-toast>

<div class="product-page p-fluid">
    <p-progressBar mode="indeterminate" class="page-progress" *ngIf="isLoading"></p-progressBar>

    <app-page-hero
        eyebrow="Order Setup"
        title="Products"
        subtitle="Maintain catalog headers and the sellable variant pricing used by purchasing and sales.">
        <div actions>
            <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" [outlined]="true" (onClick)="loadData()"></p-button>
            <p-button label="Add Product" icon="pi pi-plus" (onClick)="addProduct()"></p-button>
        </div>
    </app-page-hero>

    <app-page-metric-strip [metrics]="metrics()"></app-page-metric-strip>

    <app-page-search-panel
        title="Search Products"
        [activeFilters]="activeFilterCount()"
        [collapsed]="true"
        [resultText]="resultText()"
        (search)="applyFilters()"
        (clear)="clearFilters()">
        <div class="search-grid">
            <div class="field">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-search"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <input id="productSearch" pInputText [(ngModel)]="searchText" (keydown.enter)="applyFilters()">
                        <label for="productSearch">Name, code, description, or type</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>

            <div class="field">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <input id="productCodeFilter" pInputText [(ngModel)]="codeFilter" (keydown.enter)="applyFilters()">
                        <label for="productCodeFilter">Product code</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>

            <div class="field">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-list"></i></p-inputGroupAddon>
                    <p-iftaLabel>
                        <p-select
                            inputId="productTypeFilter"
                            [options]="productTypeOptions"
                            [(ngModel)]="typeFilter"
                            optionLabel="label"
                            optionValue="value"
                            [showClear]="true"
                            styleClass="w-full">
                        </p-select>
                        <label for="productTypeFilter">Product type</label>
                    </p-iftaLabel>
                </p-inputGroup>
            </div>
        </div>
    </app-page-search-panel>

    <app-page-data-panel
        title="Product Directory"
        subtitle="Variant-driven commercial setup, shared tax defaults, and catalog structure for each product."
        [resultText]="resultText()">
        <div actions>
            <span class="panel-note">{{ lastUpdatedText() }}</span>
        </div>

        <p-table
            [value]="filteredProducts"
            dataKey="productID"
            [expandedRowKeys]="expandedRows"
            [loading]="isLoading"
            styleClass="p-datatable-sm p-datatable-gridlines product-data-table"
            [tableStyle]="{ 'min-width': '74rem' }">
            <ng-template pTemplate="header">
                <tr>
                    <th style="width: 4rem"></th>
                    <th>Product</th>
                    <th>Pricing</th>
                    <th>Variants</th>
                    <th>Audit</th>
                    <th class="text-center" style="width: 7rem">Actions</th>
                </tr>
            </ng-template>

            <ng-template pTemplate="body" let-product let-expanded="expanded">
                <tr>
                    <td>
                        <button
                            type="button"
                            pButton
                            pRipple
                            [pRowToggler]="product"
                            class="p-button-text p-button-rounded p-button-plain"
                            [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'">
                        </button>
                    </td>
                    <td>
                        <div class="stack-cell">
                            <div class="product-title-row">
                                <a class="link-button cell-title" (click)="editProduct(product)">{{ product.name || 'Unnamed product' }}</a>
                                <p-tag [value]="product.productType || 'UNSET'" severity="info"></p-tag>
                                @if (product.systemStandard) {
                                <p-tag value="System standard" icon="pi pi-lock" severity="secondary"></p-tag>
                                }
                            </div>
                            <div class="cell-meta">
                                <span><i class="pi pi-hashtag"></i>{{ product.productCode || 'No code' }}</span>
                                <span><i class="pi pi-comment"></i>{{ product.description || 'No description' }}</span>
                                <span *ngIf="product.descriptionLocal" dir="rtl"><i class="pi pi-language"></i>{{ product.descriptionLocal }}</span>
                            </div>
                        </div>
                    </td>
                    <td>
                        <div class="stack-cell">
                            <span class="amount-pill">{{ pricingHeadline(product) }}</span>
                            <div class="cell-meta vertical">
                                <span><i class="pi pi-arrow-down"></i>Buy {{ buyingRange(product) }}</span>
                                <span><i class="pi pi-arrow-up"></i>Sell {{ sellingRange(product) }}</span>
                                <span><i class="pi pi-percentage"></i>Tax {{ formatPercent(product.taxRate) }}% / {{ currencyLabel(product) }}</span>
                            </div>
                        </div>
                    </td>
                    <td>
                        <div class="stack-cell">
                            <span class="cell-title">{{ totalVariantCount(product) }} variants</span>
                            <div class="cell-meta vertical">
                                <span><i class="pi pi-sparkles"></i>{{ variantSummary(product) }}</span>
                            </div>
                        </div>
                    </td>
                    <td>
                        <div class="stack-cell">
                            <span class="cell-title">Created {{ showDate(product.createdAt) }}</span>
                            <div class="cell-meta vertical">
                                <span><i class="pi pi-clock"></i>Updated {{ showDate(product.updatedAt) }}</span>
                                <span><i class="pi pi-id-card"></i>ID {{ product.productID || '-' }}</span>
                            </div>
                        </div>
                    </td>
                    <td class="text-center">
                        <button
                            pButton
                            type="button"
                            icon="pi pi-pencil"
                            class="p-button-rounded p-button-text p-button-info"
                            pTooltip="Edit product"
                            (click)="editProduct(product)">
                        </button>
                    </td>
                </tr>
            </ng-template>

            <ng-template pTemplate="expandedrow" let-product>
                <tr>
                    <td colspan="6" class="expanded-variants-cell">
                        <div class="variant-workspace">
                            <div class="variant-workspace-header">
                                <div>
                                    <h3>Product Variants</h3>
                                    <p>{{ totalVariantCount(product) }} variant rows priced directly on transactions.</p>
                                </div>
                            </div>

                            <p-table
                                [value]="product.productVariants || []"
                                dataKey="variantID"
                                styleClass="p-datatable-sm p-datatable-gridlines variant-table"
                                [tableStyle]="{ 'min-width': '60rem' }">
                                <ng-template pTemplate="header">
                                    <tr>
                                        <th>Variant</th>
                                        <th>Commercials</th>
                                        <th>Audit</th>
                                    </tr>
                                </ng-template>
                                <ng-template pTemplate="body" let-variant>
                                    <tr>
                                        <td>
                                            <div class="stack-cell">
                                                <span class="cell-title">{{ variant.name || 'Unnamed variant' }}</span>
                                                <div class="cell-meta">
                                                    <span><i class="pi pi-hashtag"></i>{{ variant.variantCode || 'No code' }}</span>
                                                    <span><i class="pi pi-id-card"></i>ID {{ variant.variantID || '-' }}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div class="stack-cell">
                                                <span class="amount-pill">Std {{ formatCurrency(variant.rateAdjustment ?? product.standardRate) }}</span>
                                                <div class="cell-meta vertical">
                                                    <span><i class="pi pi-arrow-down"></i>Buy {{ formatCurrency(variant.buyingRateAdjustment ?? variant.rateAdjustment ?? product.buyingRate ?? product.standardRate) }}</span>
                                                    <span><i class="pi pi-arrow-up"></i>Sell {{ formatCurrency(variant.sellingRateAdjustment ?? variant.rateAdjustment ?? product.sellingRate ?? product.standardRate) }}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div class="stack-cell">
                                                <span class="cell-title">Created {{ showDate(variant.createdAt) }}</span>
                                                <div class="cell-meta vertical">
                                                    <span><i class="pi pi-clock"></i>Updated {{ showDate(variant.updatedAt) }}</span>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </ng-template>
                                <ng-template pTemplate="emptymessage">
                                    <tr>
                                        <td colspan="3">
                                            <div class="empty-state compact-empty">
                                                <i class="pi pi-box"></i>
                                                <strong>No variants configured</strong>
                                                <span>This legacy product still depends on product-level pricing.</span>
                                            </div>
                                        </td>
                                    </tr>
                                </ng-template>
                            </p-table>
                        </div>
                    </td>
                </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
                <tr>
                    <td colspan="6">
                        <div class="empty-state">
                            <i class="pi pi-box"></i>
                            <strong>No products found</strong>
                            <span>Adjust the filters or add a new product to start building the catalog.</span>
                        </div>
                    </td>
                </tr>
            </ng-template>
        </p-table>
    </app-page-data-panel>
</div>
